import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  Spacer,
  Text,
  Textarea,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { EditButton } from './EditButton';
import { colors } from 'packages/common-frontend/colors';
import {
  SaveIconButton,
  XIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export type EditPropertyFieldOnSave = (
  props: any,
) => CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

export type EditPropertyFieldInputType = 'input' | 'textArea';

export const portfolioCovenantPropertyKeysToShow = [
  'covenantKey',
  'name',
  'description',
  'section',
] as const;

type EditablePropertyFieldProps = {
  basePropertyKey: typeof portfolioCovenantPropertyKeysToShow[number];
  fullPropertyKey: string;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  label?: string;
  inputType?: EditPropertyFieldInputType;
  isEditDisabled?: boolean;
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
};
export const EditablePropertyField = ({
  basePropertyKey,
  fullPropertyKey,
  wipConfig,
  label,
  inputType = 'input',
  isEditDisabled,
  portfolioCovenant,
}: EditablePropertyFieldProps) => {
  const initialPropertyValue = portfolioCovenant[basePropertyKey];
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [currentPropertyValue, setCurrentPropertyValue] = useState<string>(
    initialPropertyValue ?? '',
  );
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  const onSave = useCallback(
    async ({ newValue }: { newValue: string }) => {
      if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
        return;
      }

      const currentPortfolioCovenant = _.cloneDeep(portfolioCovenant);
      currentPortfolioCovenant[basePropertyKey] = newValue;

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: `${fullPropertyKey}.${basePropertyKey}`,
        updatedProperty: newValue,
        setIsSaveLoading,
      });
    },
    [
      wipConfig,
      fullPropertyKey,
      basePropertyKey,
      setIsSaveLoading,
      portfolioCovenant,
    ],
  );

  return (
    <Box mb="24px">
      {!isEditMode && (
        <>
          {label ? (
            <>
              <Flex alignItems="center" width="100%">
                <Text fontWeight={500}>{label}</Text>
                <Spacer />
                {!isEditDisabled && <EditButton setIsOpen={setIsEditMode} />}
              </Flex>
              <Text color={colors.black[70]} overflowWrap="anywhere">
                {portfolioCovenant[basePropertyKey] ?? '-'}
              </Text>
            </>
          ) : (
            <>
              <Flex alignItems="top">
                <Text color={colors.black[70]} overflowWrap="anywhere" pr="8px">
                  {portfolioCovenant[basePropertyKey] ?? '-'}
                </Text>
                <Spacer />
                {!isEditDisabled && <EditButton setIsOpen={setIsEditMode} />}
              </Flex>
            </>
          )}
        </>
      )}
      {isEditMode && !isEditDisabled && (
        <>
          {label && (
            <Text fontWeight={500} mb="16px">
              {label}
            </Text>
          )}
          <Flex alignItems="center">
            {inputType === 'input' && (
              <InputGroup>
                <Input
                  type="text"
                  value={currentPropertyValue}
                  onChange={(e) => setCurrentPropertyValue(e.target.value)}
                />
              </InputGroup>
            )}
            {inputType === 'textArea' && (
              <InputGroup>
                <Textarea
                  value={currentPropertyValue}
                  onChange={(e) => setCurrentPropertyValue(e.target.value)}
                  overflowY="scroll"
                />
              </InputGroup>
            )}

            {label ? (
              <>
                <Button
                  aria-label="Close"
                  onClick={() => {
                    setCurrentPropertyValue(initialPropertyValue ?? '');
                    setIsEditMode(false);
                  }}
                  colorScheme="aurora"
                  variant="outline"
                  borderWidth="2px"
                  mr="24px"
                  ml="32px"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    await onSave({ newValue: currentPropertyValue });
                    setIsEditMode(false);
                  }}
                  aria-label="Save"
                  colorScheme="aurora"
                  color="white"
                  isLoading={isSaveLoading}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <SaveIconButton
                  label="Save"
                  ariaLabel="save"
                  onClick={async () => {
                    await onSave({ newValue: currentPropertyValue });
                    setIsEditMode(false);
                  }}
                  isLoading={isSaveLoading}
                />
                <XIconButton
                  label="Close"
                  ariaLabel="close"
                  onClick={() => {
                    setCurrentPropertyValue(initialPropertyValue ?? '');
                    setIsEditMode(false);
                  }}
                />
              </>
            )}
          </Flex>
        </>
      )}
    </Box>
  );
};
